import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Column } from '@ant-design/charts';
import { BarChartOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Spin, Tabs, Button } from 'antd';
import { useLocation } from 'react-router-dom';
import L from 'leaflet';
import MinimizableMapControl from '../MinimizableMapControl';
import API from '../../../../API';
import { AppContext } from '../../../../context/AppContext';

const EnhancedProjectAnalytics = ({ 
  id = 'project-analytics', 
  position = 'topright',
  defaultMinimized = true,
  dock,
}) => {
  const [loading, setLoading] = useState(true);
  const [councilMetrics, setCouncilMetrics] = useState([]);
  const { TabPane } = Tabs;
  const [activeTab, setActiveTab] = useState('physical');
  const getSizing = () => {
    if (typeof window === 'undefined') {
      return { WRAPPER_HEIGHT: 280, PLOT_HEIGHT: 220, PADDING: [8, 8, 40, 28], SHOW_LABELS: true };
    }
    const w = window.innerWidth;
    if (w < 576) {
      return { WRAPPER_HEIGHT: 190, PLOT_HEIGHT: 150, PADDING: [6, 6, 44, 24], SHOW_LABELS: false };
    }
    if (w < 768) {
      return { WRAPPER_HEIGHT: 230, PLOT_HEIGHT: 190, PADDING: [8, 8, 38, 26], SHOW_LABELS: true };
    }
    return { WRAPPER_HEIGHT: 270, PLOT_HEIGHT: 230, PADDING: [8, 8, 40, 28], SHOW_LABELS: true };
  };
  const [{ WRAPPER_HEIGHT, PLOT_HEIGHT, PADDING, SHOW_LABELS }, setSizing] = useState(getSizing());
  
  const location = useLocation();
  const { selectedProject } = useContext(AppContext);
  const physicalScrollRef = useRef(null);
  const financialScrollRef = useRef(null);
  const physicalCleanupRef = useRef(null);
  const financialCleanupRef = useRef(null);
  const [physicalScrollState, setPhysicalScrollState] = useState({ canScrollLeft: false, canScrollRight: false });
  const [financialScrollState, setFinancialScrollState] = useState({ canScrollLeft: false, canScrollRight: false });

  const getContext = useCallback(() => {
    const path = location.pathname;
    const regionId = path.match(/\/regions\/(\d+)/)?.[1];
    if (regionId) return { type: 'region', id: regionId };
    return { type: 'project', id: selectedProject };
  }, [location, selectedProject]);

  const fetchMetrics = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const ctx = getContext();
      const url = ctx.type === 'region' && ctx.id
        ? `/projects/${selectedProject}/regions/${ctx.id}/councils/progress-metrics`
        : `/projects/${selectedProject}/councils/progress-metrics`;
      const resp = await API.get(url);
      const payload = resp?.data?.success ? resp.data.data : resp?.data || resp;
      const rows = payload?.councils || [];
      setCouncilMetrics(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('Error fetching councils metrics:', e);
      setCouncilMetrics([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, getContext]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const updateScrollState = useCallback((container, setState) => {
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const canScrollLeft = scrollLeft > 0;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
    setState({ canScrollLeft, canScrollRight });
  }, []);

  useEffect(() => {
    const onResize = () => {
      setSizing(getSizing());
      setTimeout(() => {
        if (physicalScrollRef.current) {
          updateScrollState(physicalScrollRef.current, setPhysicalScrollState);
        }
        if (financialScrollRef.current) {
          updateScrollState(financialScrollRef.current, setFinancialScrollState);
        }
      }, 100);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [updateScrollState]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeTab === 'physical' && physicalScrollRef.current) {
        updateScrollState(physicalScrollRef.current, setPhysicalScrollState);
      } else if (activeTab === 'financial' && financialScrollRef.current) {
        updateScrollState(financialScrollRef.current, setFinancialScrollState);
      }
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [activeTab, councilMetrics, updateScrollState]);

  const handlePhysicalScroll = useCallback(() => {
    if (physicalScrollRef.current) {
      updateScrollState(physicalScrollRef.current, setPhysicalScrollState);
    }
  }, [updateScrollState]);

  const handleFinancialScroll = useCallback(() => {
    if (financialScrollRef.current) {
      updateScrollState(financialScrollRef.current, setFinancialScrollState);
    }
  }, [updateScrollState]);

  const scrollPhysical = useCallback((direction) => {
    if (!physicalScrollRef.current) return;
    const container = physicalScrollRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    const newScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;
    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
  }, []);

  const scrollFinancial = useCallback((direction) => {
    if (!financialScrollRef.current) return;
    const container = financialScrollRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    const newScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;
    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
  }, []);

  const physicalScrollCallbackRef = useCallback((node) => {
    if (physicalCleanupRef.current) {
      physicalCleanupRef.current();
      physicalCleanupRef.current = null;
    }
    
    physicalScrollRef.current = node;
    
    if (node) {
      L.DomEvent.disableScrollPropagation(node);
      
      const stopWheel = (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      
      const stopTouch = (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      
      node.addEventListener('wheel', stopWheel, { passive: false, capture: true });
      node.addEventListener('touchmove', stopTouch, { passive: false, capture: true });
      
      updateScrollState(node, setPhysicalScrollState);
      
      physicalCleanupRef.current = () => {
        node.removeEventListener('wheel', stopWheel, { capture: true });
        node.removeEventListener('touchmove', stopTouch, { capture: true });
      };
    }
  }, [updateScrollState]);

  const financialScrollCallbackRef = useCallback((node) => {
    if (financialCleanupRef.current) {
      financialCleanupRef.current();
      financialCleanupRef.current = null;
    }
    
    financialScrollRef.current = node;
    
    if (node) {
      L.DomEvent.disableScrollPropagation(node);
      
      const stopWheel = (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      
      const stopTouch = (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      
      node.addEventListener('wheel', stopWheel, { passive: false, capture: true });
      node.addEventListener('touchmove', stopTouch, { passive: false, capture: true });
      
      updateScrollState(node, setFinancialScrollState);
      
      financialCleanupRef.current = () => {
        node.removeEventListener('wheel', stopWheel, { capture: true });
        node.removeEventListener('touchmove', stopTouch, { capture: true });
      };
    }
  }, [updateScrollState]);

  useEffect(() => {
    return () => {
      if (physicalCleanupRef.current) {
        physicalCleanupRef.current();
        physicalCleanupRef.current = null;
      }
      if (financialCleanupRef.current) {
        financialCleanupRef.current();
        financialCleanupRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cleanupFunctions = [];
    let timeoutId;
    let rafId;
    let retryCount = 0;
    const MAX_RETRIES = 5;
    
    const setupPanelListeners = () => {
      const panel = document.querySelector('.project-analytics');
      if (panel) {
        L.DomEvent.disableScrollPropagation(panel);
        
        const stopPanelWheel = (e) => {
          e.stopPropagation();
          e.stopImmediatePropagation();
        };
        
        const stopPanelTouch = (e) => {
          e.stopPropagation();
          e.stopImmediatePropagation();
        };
        
        panel.addEventListener('wheel', stopPanelWheel, { passive: false, capture: true });
        panel.addEventListener('touchmove', stopPanelTouch, { passive: false, capture: true });
        
        cleanupFunctions.push(() => {
          panel.removeEventListener('wheel', stopPanelWheel, { capture: true });
          panel.removeEventListener('touchmove', stopPanelTouch, { capture: true });
        });
        
        return true;
      }
      return false;
    };
    
    rafId = requestAnimationFrame(() => {
      const setup = () => {
        if (!setupPanelListeners() && retryCount < MAX_RETRIES) {
          retryCount++;
          timeoutId = setTimeout(setup, 100);
        }
      };
      timeoutId = setTimeout(setup, 50);
    });
    
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      cleanupFunctions.forEach(cleanup => cleanup());
      cleanupFunctions = [];
    };
  }, [councilMetrics, activeTab, loading]);

  const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const getChunkSize = () => {
    if (typeof window === 'undefined') {
      return 3;
    }
    const w = window.innerWidth;
    return w < 768 ? 2 : 3;
  };

  const getUniqueCouncils = () => {
    if (!Array.isArray(councilMetrics) || councilMetrics.length === 0) return [];
    return councilMetrics.map(c => c.council_name || `Council ${c.council_id}`);
  };

  const councilDataChunk = (prefix, councilChunk) => {
    if (!Array.isArray(councilChunk) || councilChunk.length === 0) return [];
    return councilChunk.flatMap((councilName) => {
      const council = councilMetrics.find(c => (c.council_name || `Council ${c.council_id}`) === councilName);
      if (!council) return [];
      return [
        { council: councilName, metric: 'Planned', value: Number(council[`${prefix}_planned`] || 0) },
        { council: councilName, metric: 'Actual', value: Number(council[`${prefix}_actual`] || 0) },
        { council: councilName, metric: 'Time Elapsed', value: Number(council[`${prefix}_time_elapsed`] || 0) },
      ];
    });
  };

  const renderGroupedChart = (data, colors, width = '100%') => (
    <Column
      data={data}
      xField="council"
      yField="value"
      seriesField="metric"
      isGroup
      color={colors}
      yAxis={{ label: { formatter: (v) => `${v}%` }, max: 100 }}
      label={SHOW_LABELS ? { position: 'top', formatter: (d) => `${Number(d.value).toFixed(1)}%` } : false}
      xAxis={{ 
        label: { 
          autoHide: true, 
          autoRotate: true, 
          style: { fontSize: 10 },
          formatter: (text) => {
            if (text && text.length > 20) {
              return text.slice(0, 20) + '...';
            }
            return text;
          }
        } 
      }}
      columnStyle={{ radius: [2, 2, 0, 0] }}
      padding="auto"
      appendPadding={PADDING}
      height={PLOT_HEIGHT}
      width={width}
      animation={false}
      legend={false}
    />
  );

  if (loading) {
    return (
      <MinimizableMapControl
        id={id}
        title="Project Progress"
        icon={<BarChartOutlined />}
        position={position}
        defaultMinimized={defaultMinimized}
        dock={dock}
        closable={false}
        className="chart-panel"
      >
        <div style={{ padding: 20, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      </MinimizableMapControl>
    );
  }

  return (
    <>
      <style>{`
        .project-analytics-scrollable::-webkit-scrollbar {
          height: 6px;
        }
        .project-analytics-scrollable::-webkit-scrollbar-track {
          background: transparent;
        }
        .project-analytics-scrollable::-webkit-scrollbar-thumb {
          background-color: #bfbfbf;
          border-radius: 3px;
        }
        .project-analytics-scrollable::-webkit-scrollbar-thumb:hover {
          background-color: #999;
        }
      `}</style>
      <MinimizableMapControl
        id={id}
        title="Project Progress"
        icon={<BarChartOutlined />}
        position={position}
        defaultMinimized={defaultMinimized}
        dock={dock}
        closable={false}
        className="chart-panel project-analytics"
        style={{ zIndex: 99999999, position: 'relative' }}
      >
        <div data-testid="project-analytics-panel">
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="small">
        <TabPane tab="Physical" key="physical">
          {Array.isArray(councilMetrics) && councilMetrics.length > 0 ? (
            <div style={{ position: 'relative' }} data-testid="project-analytics-physical-panel">
              {(physicalScrollState.canScrollLeft || physicalScrollState.canScrollRight) && (
                <>
                  {physicalScrollState.canScrollLeft && (
                    <Button
                      type="text"
                      icon={<LeftOutlined />}
                      onClick={() => scrollPhysical('left')}
                      style={{
                        position: 'absolute',
                        left: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        border: '1px solid #d9d9d9'
                      }}
                      size="small"
                    />
                  )}
                  {physicalScrollState.canScrollRight && (
                    <Button
                      type="text"
                      icon={<RightOutlined />}
                      onClick={() => scrollPhysical('right')}
                      style={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        border: '1px solid #d9d9d9'
                      }}
                      size="small"
                    />
                  )}
                </>
              )}
              <div 
                ref={physicalScrollCallbackRef}
                onScroll={handlePhysicalScroll}
                data-testid="project-analytics-physical-scroll"
                className="project-analytics-scrollable"
                style={{ 
                  height: WRAPPER_HEIGHT,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  display: 'flex',
                  gap: '16px',
                  padding: '8px 0',
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#bfbfbf transparent'
                }}
              >
                {(() => {
                  const councils = getUniqueCouncils();
                  const chunks = chunkArray(councils, getChunkSize());
                  return chunks.map((chunk, index) => (
                    <div 
                      key={`physical-chunk-${index}`}
                      style={{
                        minWidth: '100%',
                        flexShrink: 0,
                        height: '100%'
                      }}
                    >
                      {renderGroupedChart(
                        councilDataChunk('physical', chunk), 
                        ['#95de64', '#1890ff', '#faad14'],
                        '100%'
                      )}
                    </div>
                  ));
                })()}
              </div>
            </div>
          ) : (
            <div data-testid="project-analytics-physical-empty" style={{ height: WRAPPER_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>No analytics data available</div>
          )}
        </TabPane>
        <TabPane tab="Financial" key="financial">
          {Array.isArray(councilMetrics) && councilMetrics.length > 0 ? (
            <div style={{ position: 'relative' }} data-testid="project-analytics-financial-panel">
              {(financialScrollState.canScrollLeft || financialScrollState.canScrollRight) && (
                <>
                  {financialScrollState.canScrollLeft && (
                    <Button
                      type="text"
                      icon={<LeftOutlined />}
                      onClick={() => scrollFinancial('left')}
                      style={{
                        position: 'absolute',
                        left: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        border: '1px solid #d9d9d9'
                      }}
                      size="small"
                    />
                  )}
                  {financialScrollState.canScrollRight && (
                    <Button
                      type="text"
                      icon={<RightOutlined />}
                      onClick={() => scrollFinancial('right')}
                      style={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        border: '1px solid #d9d9d9'
                      }}
                      size="small"
                    />
                  )}
                </>
              )}
              <div 
                ref={financialScrollCallbackRef}
                onScroll={handleFinancialScroll}
                data-testid="project-analytics-financial-scroll"
                className="project-analytics-scrollable"
                style={{ 
                  height: WRAPPER_HEIGHT,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  display: 'flex',
                  gap: '16px',
                  padding: '8px 0',
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#bfbfbf transparent'
                }}
              >
                {(() => {
                  const councils = getUniqueCouncils();
                  const chunks = chunkArray(councils, getChunkSize());
                  return chunks.map((chunk, index) => (
                    <div 
                      key={`financial-chunk-${index}`}
                      style={{
                        minWidth: '100%',
                        flexShrink: 0,
                        height: '100%'
                      }}
                    >
                      {renderGroupedChart(
                        councilDataChunk('financial', chunk), 
                        ['#b37feb', '#13c2c2', '#faad14'],
                        '100%'
                      )}
                    </div>
                  ));
                })()}
              </div>
            </div>
          ) : (
            <div data-testid="project-analytics-financial-empty" style={{ height: WRAPPER_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
              No financial progress data
            </div>
          )}
        </TabPane>
      </Tabs>
      </div>
      </MinimizableMapControl>
    </>
  );
};

export default EnhancedProjectAnalytics;
